
/* eslint-disable camelcase */
import Vue from 'vue'
import * as imgproxy from '@made-people/imgproxy'
import { mapGetters } from 'vuex'

export default Vue.extend<{}, Methods, Computed, FacetInfoContainerProps>({
  name: 'FacetInfoContainer',
  props: {
    countries: {
      type: Array,
      default: () => (['_all_countries']),
    },
    content: {
      type: Array,
      default: () => ([]),
    },
    matchBlock: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => ({
        title: '',
        description: '',
        og_description: '',
        og_image: '',
        og_title: '',
        twitter_description: '',
        twitter_image: '',
        twitter_title: '',
      }),
    },
    facetMatcher: {
      type: Object,
      default: () => ({
        facetMatcher: '',
      }),
    },
  },
  head () {
    return Object.assign({},
      this.fullTitle ? { title: this.fullTitle } : {},
      this.metaFormatted ? { meta: this.metaFormatted } : {},
    )
  },
  computed: {
    ...mapGetters({
      settings: 'storyblok/settings',
    }),
    fullTitle () {
      return (this.settings?.meta_title_prefix ?? '') +
        this.meta.title +
        (this.settings?.meta_title_suffix ?? '')
    },
    metaFormatted () {
      const {
        title,
        description,
        og_description,
        og_image,
        og_title,
        twitter_description,
        twitter_image,
        twitter_title,
      } = (this.meta ?? {})

      const metaTags: MetaTag[] = [
        {
          hid: 'og:type',
          name: 'og:type',
          content: 'website',
        }
      ]

      this.addMetaTag(metaTags, { name: 'description' }, description)
      this.addMetaTag(metaTags, { name: 'og:title' }, og_title || title)
      this.addMetaTag(metaTags, { name: 'og:description' }, og_description || description)
      this.addMetaTag(metaTags, { name: 'twitter:title' }, twitter_title || title)
      this.addMetaTag(metaTags, { name: 'twitter:description' }, twitter_description || description)
      this.addMetaTag(metaTags, { property: 'og:image' }, this.getImageUrl(og_image || twitter_image))
      this.addMetaTag(metaTags, { property: 'twitter:image' }, this.getImageUrl(twitter_image || og_image))
      return metaTags
    },
  },
  methods: {
    addMetaTag (metaTags: MetaTag[], { name, property }: PropertyOrName, content?: string | null) {
      if (
        (typeof name !== 'undefined' && typeof property !== 'undefined') ||
        (typeof name === 'undefined' && typeof property === 'undefined')
      ) {
        // eslint-disable-next-line unicorn/prefer-type-error
        throw new Error('[addMetaTag]: Either name or property must be defined')
      }
      if (content) {
        metaTags.push({
          hid: name ?? property,
          [typeof name !== 'undefined' ? 'name' : 'property']: name ?? property,
          content,
        } as unknown as MetaTag)
      }
    },
    getImageUrl (image: string) {
      imgproxy.useBaseUrl(this.$config.imageProxyUrl)
      return imgproxy.transform(image, 'resize:fill/w:1200/h:630/g:sm/q:70')
    },
  },
})

type Content = Array<{
  component: string;
  _uid: string;
}>

export interface Meta {
  title: string;
  description: string;
  og_description: string;
  og_image: string;
  og_title: string;
  twitter_description: string;
  twitter_image: string;
  twitter_title: string;
}

export interface FacetMatcher {
  facetMatcher: string;
}

interface Computed {
  fullTitle: string;
  settings: Partial<Record<string, string>>;
  metaFormatted: any;
}

interface Methods {
  addMetaTag: (metaTags: MetaTag[], { name, property }: PropertyOrName, content?: string | null) => void;
  getImageUrl: (image: string) => string | null;
}

export interface FacetInfoContainerProps {
  countries: string[];
  content: Content;
  matchBlock: Content;
  meta: Meta;
  facetMatcher: FacetMatcher;
}

interface MetaTag {
  hid: string;
  name: string;
  content?: string;
  property?: string;
}

type PropertyOrName =
  | ({ name?: never } & Required<Pick<MetaTag, 'property'>>)
  | ({ property?: never } & Required<Pick<MetaTag, 'name'>>)

